import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import FlagIcon from './FlagIcon';
import {
  getFlagCode,
  VOICE_THEME as t,
  uniqueLanguages,
  translate,
  LANGUAGE_PT,
  ACCENT_PT,
} from '../../utils/voiceMetadata';

/**
 * LanguagePreviewDropdown — dropdown estilo ElevenLabs pra escolher idioma do
 * preview de uma voz.
 *
 * FASE 2 ITER3.4: a LISTA aberta é renderizada via createPortal pro document.body
 * com position:fixed. Isso resolve o bug onde cards seguintes do grid cobriam
 * a lista (problema de stacking context). Funciona idêntico tanto pro modo
 * `inline=true` (header da voz central + cards do modal) quanto pro modo
 * `inline=false` (modal de detalhes — atualmente não usado).
 *
 * Props:
 *   - voice: objeto da voz (precisa ter voice_info.verified_languages)
 *   - inline: true = visual compacto (menor); false = full (modal)
 *   - onPlayLanguage(language, accent, previewUrl, languageKey)
 *   - currentLanguageKey: string|null pro check ✓
 */
export default function LanguagePreviewDropdown({
  voice,
  inline = true,
  onPlayLanguage,
  currentLanguageKey = null,
  compactLabel = false,  // ITER4: quando true, botão fechado mostra só "Inglês" (sem "Pré-visualização de")
}) {
  // Cores via CSS vars (adaptam automaticamente ao tema — sem useTheme)
  const [open, setOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const info = voice && voice.voice_info;
  const verifiedLanguages = info && Array.isArray(info.verified_languages) ? info.verified_languages : [];

  const entries = useMemo(
    () => uniqueLanguages(verifiedLanguages).filter((e) => e.preview_url),
    [verifiedLanguages],
  );

  const currentEntry = useMemo(() => {
    if (currentLanguageKey) {
      const hit = entries.find((e) => `${e.language}|${e.accent || ''}` === currentLanguageKey);
      if (hit) return hit;
    }
    if (info && info.language) {
      const primary = entries.find((e) => (e.language || '').toLowerCase() === info.language.toLowerCase());
      if (primary) return primary;
    }
    return entries[0] || null;
  }, [entries, currentLanguageKey, info]);

  // Recalcula posição do botão (chamado ao abrir e em resize)
  const updateRect = useCallback(() => {
    if (!buttonRef.current) return;
    setButtonRect(buttonRef.current.getBoundingClientRect());
  }, []);

  // ESC + click fora + scroll externo fecham (ITER4.4: scroll INTERNO no menu NÃO fecha)
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      const insideButton = wrapperRef.current && wrapperRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideButton && !insideMenu) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    // Fecha SÓ se o scroll é fora do menu (scroll na scrollbar interna deve ser permitido)
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return; // scroll interno: ignora
      setOpen(false);
    };
    const onResize = () => updateRect();

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true); // capture: pega scroll de qualquer container
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, updateRect]);

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
    } else {
      updateRect();
      setOpen(true);
    }
  };

  if (!entries.length || !info) return null;

  const handleSelect = (entry) => {
    const key = `${entry.language}|${entry.accent || ''}`;
    setOpen(false);
    if (onPlayLanguage) {
      onPlayLanguage(entry.language, entry.accent || null, entry.preview_url, key);
    }
  };

  const sizes = inline
    ? {
        btnPadding: '5px 10px',
        btnFontSize: '11px',
        flagSize: '14px',
        itemPadding: '7px 10px',
        itemFontSize: '11.5px',
        itemGap: '8px',
      }
    : {
        btnPadding: '10px 14px',
        btnFontSize: '13px',
        flagSize: '18px',
        itemPadding: '10px 12px',
        itemFontSize: '13px',
        itemGap: '10px',
      };

  const buttonLabel = currentEntry
    ? (compactLabel
        ? translate(currentEntry.language, LANGUAGE_PT)
        : `Pré-visualização de ${translate(currentEntry.language, LANGUAGE_PT)}`)
    : (compactLabel ? 'Idiomas' : 'Pré-visualização');

  // Posição/dimensão do menu portal — calculado quando aberto
  const menuStyles = buttonRect ? computeMenuPosition(buttonRect, inline) : null;

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', display: inline ? 'inline-block' : 'block', width: inline ? 'auto' : '100%' }}
    >
      <button
        ref={buttonRef}
        onClick={toggleOpen}
        style={{
          width: inline ? 'auto' : '100%',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: sizes.btnPadding,
          borderRadius: '8px',
          border: open ? '1px solid var(--dp-blue-500)' : '1px solid var(--dp-border-medium)',
          background: open
            ? 'linear-gradient(135deg, rgba(13,174,255,0.10), rgba(13,174,255,0.04))'
            : 'var(--dp-bg-card, transparent)',
          color: 'var(--dp-text-primary)',
          fontSize: sizes.btnFontSize,
          fontFamily: 'var(--dp-font)',
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.18s',
          textAlign: 'left',
          justifyContent: inline ? 'flex-start' : 'space-between',
        }}
        onMouseEnter={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--dp-blue-500)'; }}
        onMouseLeave={(e) => { if (!open) e.currentTarget.style.borderColor = 'var(--dp-border-medium)'; }}
        title={buttonLabel}
      >
        {currentEntry && (
          <FlagIcon
            code={getFlagCode(currentEntry.language, currentEntry.accent, currentEntry.locale)}
            size={sizes.flagSize}
            rounded
          />
        )}
        <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {buttonLabel}
        </span>
        <ChevronDown
          size={inline ? 12 : 16}
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            opacity: 0.7,
            flexShrink: 0,
          }}
        />
      </button>

      {/* Menu via portal (resolve stacking context). Cores adaptativas ao tema. */}
      {open && menuStyles && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: `${menuStyles.top}px`,
            left: `${menuStyles.left}px`,
            width: `${menuStyles.width}px`,
            maxHeight: '320px',
            overflowY: 'auto',
            background: t.bg,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: `1px solid ${t.border}`,
            borderRadius: '10px',
            boxShadow: t.shadow,
            zIndex: 1300,
            padding: '4px',
            fontFamily: 'var(--dp-font)',
            animation: 'agFadeInUp 0.15s ease-out',
            color: t.text,
          }}
          className="ag-scrollbar"
          onClick={(e) => e.stopPropagation()}
        >
          {entries.map((entry, idx) => {
            const key = `${entry.language}|${entry.accent || ''}`;
            const isCurrent = currentEntry && key === `${currentEntry.language}|${currentEntry.accent || ''}`;
            const langPt = translate(entry.language, LANGUAGE_PT);
            const accPt = entry.accent && entry.accent.toLowerCase() !== 'standard'
              ? ` (${translate(entry.accent, ACCENT_PT)})`
              : '';
            return (
              <button
                key={`${key}|${idx}`}
                role="option"
                aria-selected={isCurrent}
                onClick={() => handleSelect(entry)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: sizes.itemGap,
                  padding: sizes.itemPadding,
                  borderRadius: '6px',
                  border: 'none',
                  background: isCurrent ? 'rgba(13,174,255,0.12)' : 'transparent',
                  color: isCurrent ? 'var(--dp-blue-500)' : t.text,
                  cursor: 'pointer',
                  fontSize: sizes.itemFontSize,
                  fontFamily: 'var(--dp-font)',
                  fontWeight: isCurrent ? 600 : 400,
                  textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = t.hover; }}
                onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: '14px', display: 'inline-flex', justifyContent: 'center', flexShrink: 0 }}>
                  {isCurrent && <Check size={12} />}
                </span>
                <FlagIcon code={getFlagCode(entry.language, entry.accent, entry.locale)} size={sizes.flagSize} rounded />
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Pré-visualização de {langPt}{accPt}
                </span>
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

/**
 * Computa posição absoluta do menu em viewport, com:
 * - width: max(buttonRect.width, 240) — pra header inline pequeno expandir
 * - flip vertical se passar do bottom (abre pra cima)
 * - clamp horizontal se passar da direita
 */
function computeMenuPosition(rect, inline) {
  const PAD = 8;
  const GAP = 4;
  const MIN_WIDTH = inline ? 240 : 320;
  const MAX_WIDTH = 380;
  const MENU_HEIGHT_ESTIMATE = 320;
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  // Width: largura do botão, mas pelo menos MIN_WIDTH, até MAX_WIDTH
  let width = Math.min(MAX_WIDTH, Math.max(rect.width, MIN_WIDTH));

  // Horizontal: alinha à esquerda do botão; se passar da direita do viewport,
  // alinha à direita do botão.
  let left = rect.left;
  if (left + width + PAD > viewportW) {
    left = Math.max(PAD, rect.right - width);
  }
  // Garante que não passe da esquerda
  if (left < PAD) left = PAD;

  // Vertical: abrir abaixo. Se não couber, abrir acima.
  let top = rect.bottom + GAP;
  const spaceBelow = viewportH - rect.bottom - PAD;
  const spaceAbove = rect.top - PAD;
  if (spaceBelow < 180 && spaceAbove > spaceBelow) {
    // Abre pra cima
    top = Math.max(PAD, rect.top - GAP - Math.min(MENU_HEIGHT_ESTIMATE, spaceAbove));
  }
  return { top, left, width };
}
